// Copyright © 2026 ApexRuntime Labs by Shashi (https://github.com/spshah). All rights reserved.

import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import categoryAPI from '../../api/services/budget/categoryService';

export default function AllocationForm({ periodId, initial, onSubmit, onClose, periodAllocations = [] }) {
  const [form, setForm] = useState({
    category: initial?.category || '',
    allocated_amount: initial?.allocated_amount || '',
  });
  const [allCategories, setAllCategories] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCats, setLoadingCats] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadCategories();
  }, [periodAllocations]);

  const loadCategories = async () => {
    setLoadingCats(true);
    try {
      // Load all expense categories
      const res = await categoryAPI.list({ active: 'true' });
      const allCats = res.data || [];
      setAllCategories(allCats);

      // Filter out categories that already have allocations in this period
      const allocatedCategoryIds = new Set(
        (periodAllocations || []).map((a) => a.category)
      );
      const available = allCats.filter(
        (c) => !allocatedCategoryIds.has(c.id)
      );
      setAvailableCategories(available);
    } catch (err) {
      console.error('Failed to load categories:', err);
    } finally {
      setLoadingCats(false);
    }
  };

  const set = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.category || !form.allocated_amount) {
      setError('Please fill in all fields');
      return;
    }

    const amount = parseFloat(form.allocated_amount);
    if (isNaN(amount) || amount <= 0) {
      setError('Allocation amount must be greater than 0');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await onSubmit({
        category: parseInt(form.category),
        allocated_amount: amount,
      });
    } catch (err) {
      // Handle both string errors and object errors from backend
      let errorMsg = 'Failed to save allocation';
      if (err.response?.data) {
        // Backend validation errors
        const data = err.response.data;
        if (typeof data === 'string') {
          errorMsg = data;
        } else if (data.category) {
          errorMsg = Array.isArray(data.category) ? data.category[0] : data.category;
        } else if (data.allocated_amount) {
          errorMsg = Array.isArray(data.allocated_amount) ? data.allocated_amount[0] : data.allocated_amount;
        } else if (data.error) {
          errorMsg = data.error;
        } else {
          errorMsg = JSON.stringify(data);
        }
      } else if (err.message) {
        errorMsg = err.message;
      }
      setError(errorMsg);
      console.error('Allocation error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-gray-900">
          {initial ? 'Edit' : 'New'} Allocation
        </h2>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 text-gray-400"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Error */}
      {error && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
          {error}
        </p>
      )}

      {/* Category */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Category
        </label>
        {availableCategories.length === 0 && !loadingCats && !initial ? (
          <p className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3">
            All categories already have allocations in this period.
          </p>
        ) : (
          <select
            value={form.category}
            onChange={set('category')}
            required
            disabled={loadingCats || (availableCategories.length === 0 && !initial)}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">Select a category…</option>
            {availableCategories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.icon} {c.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Amount */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Amount to Allocate (₹) <span className="text-red-500">*</span>
        </label>
        <input
          type="number"
          min="0.01"
          step="0.01"
          placeholder="e.g., 5000.00"
          value={form.allocated_amount}
          onChange={set('allocated_amount')}
          required
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        <p className="text-xs text-gray-500 mt-1">Must be greater than 0</p>
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={loading || loadingCats}
        className="w-full py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {loading ? 'Saving…' : 'Save Allocation'}
      </button>
    </form>
  );
}
